import React, { useEffect, useMemo } from 'react';
import { AudioElement } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import { IconCreateGroup } from '@bynder/icons';
import useEditor from '../../../../../hooks/useEditor';
import useForceUpdate from '../../../../../hooks/useForceUpdate';
import useGeneralShortcuts from 'packages/hooks/shortcuts/useGeneralShortcuts';
import generateTestId from 'packages/helpers/testIdHelpers';
import { TimelineActionsIcon } from '../TimelineActions.styled';
import useElementGeneralActions from 'packages/pages/editor/useElementGeneralActions';
import MaybeTooltip from 'packages/common/MaybeTooltip';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';

const testId = generateTestId('timeline_actions_group');

function GroupAction() {
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();
    const { manipulationRenderer } = useEditor();
    const selectedElements = manipulationRenderer?.getSelectedElements() || [];
    const { handleCreateGroup } = useElementGeneralActions();

    const isDisabledByAudio = useMemo(
        () => selectedElements.some((el) => el instanceof AudioElement),
        [selectedElements],
    );
    const isActive = !!selectedElements.length && !isDisabledByAudio;

    useGeneralShortcuts({ handleGroup: handleCreateGroup });

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        manipulationRenderer.eventEmitter.on('elementSelected', forceUpdate);

        return () => {
            manipulationRenderer.eventEmitter.off('elementSelected', forceUpdate);
        };
    }, [forceUpdate, manipulationRenderer]);

    return (
        <MaybeTooltip
            position="top-right"
            hasTooltip={isDisabledByAudio}
            content={translate('editor.timeline.element.actions.group.tooltip')}
        >
            <div
                title={`${translate('editor.timeline.element.actions.group')} (${parseShortcutForTooltip([
                    'cmd',
                    'g',
                ])})`}
            >
                <TimelineActionsIcon onClick={handleCreateGroup} active={isActive} {...testId}>
                    <IconCreateGroup />
                </TimelineActionsIcon>
            </div>
        </MaybeTooltip>
    );
}

export default GroupAction;
